import * as angular from "angular";

export class AdminCampaignService {
    private $http: any;
    private apiRoot: string;

    constructor($http: any, __env: any) {
        this.$http = $http;
        this.apiRoot = __env.apiUrl() + "/campaign";
    }

    /**
     * `campaignuser` makes the backend inner-join campaignuser for the
     * logged-in admin, so only accounts they are a user on come back. Sent as
     * its own param rather than inside `filter`, because
     * Filter.nonEmptyValues() drops plain booleans.
     */
    all(
        data: any,
        offset: any,
        timeout?: any,
        campaignuser?: boolean,
        thisRegion?: string | boolean | null,
        exportAll?: boolean,
        sort?: string | null,
        dir?: string,
        activity?: {
            lens?: string;
            range?: number;
            trend?: string;
        } | null
    ): Promise<object> {
        const isActivity = !!activity && activity.lens === "activity";
        return this.$http.get(this.apiRoot + "/admin_all", {
            params: {
                filter: data,
                // Sorted lists page by row offset, unsorted ones by last id.
                offset: offset,
                campaignuser: campaignuser ? true : undefined,
                thisRegion: thisRegion || undefined,
                export: exportAll ? true : undefined,
                sort: sort || undefined,
                dir: sort ? dir : undefined,
                // AV-17958 Activity lens
                lens: isActivity ? "activity" : undefined,
                range: isActivity ? activity!.range || 7 : undefined,
                trend:
                    isActivity && activity!.trend ? activity!.trend : undefined,
            },
            timeout: timeout,
        });
    }

    /**
     * Chip counts for the list, fetched alongside it rather than inside it,
     * so the rows never wait on the counting query. Same params as all() so
     * the numbers describe the same rows.
     */
    chipCounts(
        data: any,
        campaignuser?: boolean,
        thisRegion?: string | boolean | null,
        activity?: { lens?: string; range?: number; trend?: string } | null
    ): Promise<object> {
        const isActivity = !!activity && activity.lens === "activity";
        return this.$http.get("/v2/campaign/admin_chip_counts", {
            params: {
                filter: data,
                campaignuser: campaignuser ? true : undefined,
                thisRegion: thisRegion || undefined,
                lens: isActivity ? "activity" : undefined,
                range: isActivity ? activity!.range || 7 : undefined,
                trend:
                    isActivity && activity!.trend ? activity!.trend : undefined,
            },
        });
    }

    /**
     * Activity lens numbers for the rows already loaded (AV-17958), fetched
     * after them so the page never waits on the aggregate.
     */
    activityStats(ids: Array<number | string>, range: number): Promise<object> {
        return this.$http.get("/v2/campaign/admin_activity_stats", {
            params: { ids: ids.join(","), range: range },
        });
    }

    /**
     * Activity lens rows (AV-17958): id, name and organisation only, ordered
     * and filtered by the nightly aggregate. Same params as all(); the
     * numbers come from activityStats() afterwards.
     */
    activity(
        data: any,
        offset: any,
        timeout?: any,
        campaignuser?: boolean,
        thisRegion?: string | boolean | null,
        sort?: string | null,
        dir?: string,
        activity?: { lens?: string; range?: number; trend?: string } | null
    ): Promise<object> {
        return this.$http.get("/v2/campaign/admin_activity", {
            params: {
                filter: data,
                offset: offset,
                campaignuser: campaignuser ? true : undefined,
                thisRegion: thisRegion || undefined,
                sort: sort || undefined,
                dir: sort ? dir : undefined,
                lens: "activity",
                range: (activity && activity.range) || 7,
                trend: activity && activity.trend ? activity.trend : undefined,
            },
            timeout: timeout,
        });
    }

    update(data: any): Promise<object> {
        return this.$http.post("/v2/campaign/update/" + data.campaignid, data);
    }

    cancel(data: any): Promise<object> {
        return this.$http.post(this.apiRoot + "/cancelCampaign", data);
    }

    reactivate(data: any): Promise<object> {
        data.id = "";
        return this.$http.post(this.apiRoot + "/reactivate/" + data.id, data);
    }

    enableExport(data: any): Promise<object> {
        return this.$http.post("/v2/campaign/enableexport", data);
    }

    enableAPI(data: any): Promise<object> {
        return this.$http.post("/v2/campaign/enableapi", data);
    }

    enableProspector(data: any): Promise<object> {
        return this.$http.post("/v2/campaign/enable_prospector", data);
    }

    toggleReview(data: any): Promise<object> {
        return this.$http.post("/v2/campaign/togglereview", data);
    }

    enable;

    geocode(data: any): Promise<object> {
        return this.$http.post("/v2/campaign/geocode", data);
    }
}

angular
    .module("vcServices")
    .service("AdminCampaignService", AdminCampaignService);
